import { User } from '@src/entities/User/ui/components/User';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { NumberInput } from '@uhg-abyss/web/ui/NumberInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { isEmpty, merge, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';
import { Styles } from './includes/styles';

const Components = {
  NumberInput,
  SelectInput,
};

/**
 * Table
 *
 * Provides the user with a table to specify repeatable fields to search risk records by.
 *
 * @param accessor
 * @param disabledFields
 * @param fields
 * @param remove
 * @param permissions
 * @param repeatableFields
 * @param rows
 * @returns {Element}
 * @constructor
 */
export function Table({
  accessor = '',
  disabledFields = {},
  fields = [],
  remove = () => {},
  permissions = {
    add: {
      attributes: [],
      isAllowed: null,
      resource: '',
    },
    edit: {
      attributes: [],
      isAllowed: null,
      resource: '',
    },
    remove: {
      attributes: [],
      isAllowed: null,
      resource: '',
    },
  },
  repeatableFields = [],
  rows = [],
}) {
  /**
   * renderCellField
   *
   * @TODO Needs description.
   *
   * @param args
   * @returns {JSX.Element}
   */
  const renderCell = (args) => {
    const { row } = args;
    const { index } = row;

    const theField = fields?.find((field) => {
      return field?.model === args?.column?.id;
    });

    const TheComponent = Components[theField?.component];

    return (
      <User.Capability
        alias="edit"
        attributes={permissions?.edit?.attributes}
        isAllowed={permissions?.edit?.isAllowed}
        resource={permissions?.edit?.resource}
      >
        <TheComponent
          {...{
            ...theField,
            ...{
              isDisabled: disabledFields?.[index]?.includes(theField?.model),
              label: ' 123',
              model: `${accessor}[${index}][${theField?.model}]`,
            },
          }}
        />
      </User.Capability>
    );
  };

  /**
   * renderCellActions
   *
   * @TODO Needs description.
   *
   * @param args
   * @returns {JSX.Element}
   */
  const renderCellActions = (args) => {
    const { row } = args;
    const { index } = row;

    if (index === 0) {
      return null;
    }

    return (
      <User.Capability
        alias="remove"
        attributes={permissions?.remove?.attributes}
        isAllowed={permissions?.remove?.isAllowed}
        resource={permissions?.remove?.resource}
      >
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          className="removeRow"
          onClick={() => {
            remove(index);
          }}
          variant="custom"
        >
          <Flex alignItems="center">
            <div>
              <IconSymbol icon="cancel" variant="filled" />
              <IconSymbol icon="cancel" variant="outlined" />
            </div>
            <div>Remove</div>
          </Flex>
        </Link>
      </User.Capability>
    );
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    const theColumns = orderBy(fields, ['order'], ['asc'])?.map((field) => {
      return {
        accessor: field?.model,
        Cell: renderCell,
        disableSortBy: true,
        Header: field?.label,
        order: field?.order,
      };
    });

    theColumns.push({
      accessor: 'actions',
      Cell: renderCellActions,
      disableSortBy: true,
      Header: ' ',
    });

    return theColumns;
  }, [fields, permissions?.edit?.isAllowed, permissions?.remove?.isAllowed]);

  return (
    <ErrorHandler location="src/shared/ui/components/RepeatableFields/components/Table/Table.jsx">
      {!isEmpty(fields) && !isEmpty(repeatableFields) ? (
        <Styles>
          <TableComponent
            {...{
              columns,
              configuration: merge({}, configuration, {
                data: repeatableFields,
                reorderRows: false,
              }),
              dataKey: accessor,
              rows,
            }}
          />
        </Styles>
      ) : (
        <React.Fragment />
      )}
    </ErrorHandler>
  );
}
